import { useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { Pill } from '../pill';
import { PillButtons, ToggleCaret } from '../input';
import { Loading } from '../loading';
import { Progress } from '../job/Progress';
import { Cost } from '../job/Cost';
import { TableFromObject } from '../table';
import { Messages } from './Messages';
import { styles } from '../../constants';

const fontSize = 12;

const header = ({ label, loading, open, setOpen, preview }) => (
  <div
    onClick={() => setOpen(!open)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 13,
      fontWeight: 'bold',
      color: '#606060',
      cursor: 'pointer',
    }}
  >
    <div style={{ border: '0px solid blue' }}>
      <ToggleCaret open={open} size={16} />
    </div>
    <div style={{ display: 'flex', alignItems: 'center', width: fontSize * 1.1 }}>
      {loading ? <Loading size={fontSize * 1.1} /> : <FaCheckCircle />}
    </div>
    <div>{label}</div>
    {!open && preview}
  </div>
);

const section = ({ label, body }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
    <div style={{ width: 100 }}>
      <Pill blue style={{ border: 0 }}>
        {label}
      </Pill>
    </div>
    <div style={{ flex: 1, fontSize }}>{body}</div>
  </div>
);

const vcenter = (it) => (
  <div
    style={{
      paddingTop: 4,
      alignItems: 'center',
      display: '-webkit-box',
      WebkitLineClamp: 5,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}
  >
    {it}
  </div>
);

const infoStyle = {
  display: 'flex',
  flexDirection: 'column',
  borderTop: '1px solid #e3e3e3',
  gap: 4,
  paddingTop: 10,
};

export const PlanInfo = ({ job }) => {
  const [open, setOpen] = useState(false);

  if (!job?.progress?.plan) {
    return null;
  }
  const { method, params, loading } = job.progress.plan;

  const nodes = Object.keys(params || {})
    .filter((key) => !['method', 'signalId', 'jobId', 'template', 'startUrls', 'urls'].includes(key))
    .map((key) => section({ label: key, body: vcenter(JSON.stringify(params[key])) }));

  const templateBody = (template) => {
    if (typeof template == 'string') {
      return <div>{template}</div>;
    } else if (Array.isArray(template)) {
      return <div>{JSON.stringify(template)}</div>;
    } else {
      return <TableFromObject obj={template} cellStyle={{ fontSize }} />;
    }
  };

  const body = (
    <>
      {method && section({ label: 'method', body: vcenter(method) })}
      {params?.urls &&
        section({
          label: 'urls',
          body: vcenter(
            <div>
              {params.urls.slice(0, 100).map((it) => (
                <div>{it}</div>
              ))}
            </div>,
          ),
        })}
      {params?.startUrls &&
        section({
          label: 'startUrls',
          body: vcenter(
            <div>
              {params.startUrls.slice(0, 100).map((it) => (
                <div>{it}</div>
              ))}
            </div>,
          ),
        })}
      {params?.template &&
        section({
          label: 'template',
          body: templateBody(params?.template),
        })}
      <div>{nodes}</div>
    </>
  );

  const preview = (
    <div style={{ display: 'flex', fontWeight: 'normal', gap: 5, ...styles.textOverflowStyle }}>
      {params?.urls && (
        <div>
          Go to {/*params.urls[0]*/}
          {params.urls.length == 1 ? params.urls[0] : params.urls.length + ' URLs'} and
        </div>
      )}
      {params?.url && <div>Go to {params?.url}</div>}
      {method && <div>{method}</div>}
      {params?.query && <div style={{ fontWeight: 'bold' }}>{params.query}</div>}
      {params?.template && typeof params.template == 'string' && (
        <div style={{ ...styles.textOverflowStyle, fontWeight: 'bold' }}>{params?.template}</div>
      )}
      {params?.template && typeof params.template != 'string' && (
        <div style={{ ...styles.textOverflowStyle, fontWeight: 'bold' }}>{Object.keys(params.template).join(', ')}</div>
      )}
    </div>
  );

  return (
    <div style={infoStyle}>
      {header({ label: 'Plan', loading, open, setOpen, preview })}
      {open && body}
    </div>
  );
};

const ExtractInfo = ({ job }) => {
  const [open, setOpen] = useState(false);

  if (!job?.progress?.extract) {
    return null;
  }

  const { started, completed, loading } = job.progress.extract;

  const preview = started && (
    <div style={{ display: 'flex', fontWeight: 'normal', gap: 5, ...styles.textOverflowStyle }}>
      {completed || 0}/{started}
    </div>
  );
  const body = (
    <>
      {(started || completed) &&
        section({
          label: 'extractions',
          body: <TableFromObject obj={{ started, completed }} cellStyle={{ fontSize }} />,
        })}
    </>
  );
  return (
    <div style={infoStyle}>
      {header({ label: 'Extract', loading, open, setOpen, preview })}
      {open && body}
    </div>
  );
};

const CrawlInfo = ({ job }) => {
  const [open, setOpen] = useState(false);

  if (!job?.progress?.crawl) {
    return null;
  }
  const { started, completed, consecutive, target, loading } = job.progress.crawl;

  const preview = started && (
    <div style={{ display: 'flex', fontWeight: 'normal', gap: 5, ...styles.textOverflowStyle }}>
      {completed || 0}/{started}
    </div>
  );

  const body = (
    <>
      {(started || completed) &&
        section({
          label: 'visits',
          body: <TableFromObject obj={{ started, completed }} cellStyle={{ fontSize }} />,
        })}
      {(consecutive || target) &&
        section({
          label: 'zeroHits',
          body: <TableFromObject obj={{ consecutive, cap: target }} cellStyle={{ fontSize }} />,
        })}
    </>
  );

  return (
    <div style={infoStyle}>
      {header({ label: 'Crawl', loading, open, setOpen, preview })}
      {open && body}
    </div>
  );
};

export const Info = ({ job }) => {
  const [tab, setTab] = useState('status');

  const choices = [
    ['status', 'Status'],
    // ['messages', 'Messages'],
    ['jobs', 'Progress'],
    ['cost', 'Cost'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <PillButtons size="sm" value={tab} onChange={setTab} choices={choices} />

      {tab == 'status' && <PlanInfo job={job} />}
      {tab == 'status' && <CrawlInfo job={job} />}
      {tab == 'status' && <ExtractInfo job={job} />}

      {tab == 'messages' && <Messages job={job} />}
      {tab == 'jobs' && <Progress job={job} />}
      {tab == 'cost' && <Cost job={job} showPercent />}
    </div>
  );
};
